import { authConstants } from './actionTypes';
import { NotificationStatus, AppErrorCode } from '../app/actionTypes';
import {
    history,
    UserStorageHelper,
    logger,
    Utilities,
    LocalStorageHelper,
    getValidatedMappedLicenseStatusBody,
    getValidatedMappedLicenseStatusBodyCache,
} from '../../../helpers';
import { store, AppDispatch } from '../../../store';
import {
    nextcloudApi,
    NextcloudUser,
    License,
    Settings,
    LicenseLevel,
    LicensePermissions,
    Assembly,
    NextcloudThemingCapabilities,
    DEFAULT_THEME_COLORS,
} from '@sendent/nextcloud-api';
import { appApi } from '../../../services/appApi';
import { OperationId, startFetch, stopFetch } from '../app/actionCreators';
import { areResponsesSuccessful } from '../../../helpers/validation';
import { UserConfig } from '../../reducers/auth/reducer';

// When the server didn't return a clean error, the api library falls back
// to a generic string with the status code and raw response body baked in.
// Pull the server's own `message` out of that raw body if there is one,
// otherwise treat the error as having no user-presentable message.
function getCleanLoginErrorMessage(message?: string): string | undefined {
    const embeddedBody = message?.match(/responded with status code \d+ (.*)$/);
    if (!embeddedBody) return undefined;

    try {
        const parsed = JSON.parse(embeddedBody[1]);
        return typeof parsed.message === 'string' ? parsed.message : undefined;
    } catch {
        return undefined;
    }
}

/** Result of nextcloudApi.loginInit's login-flow v2 response */
type LoginInitResult = { login: string; poll: unknown };

/**
 * Sets a settings flag, and warns instead of crashing when the
 * server-provided settings definition is missing the entry.
 */
const setSettingFlag = (
    settings: Settings,
    name: string,
    flag: 'display' | 'enabled',
): void => {
    const setting = settings.getSettingsByName(name);
    if (!setting) {
        logger.warn(`Setting '${name}' missing from definition`, 'Auth');
        return;
    }
    setting[flag] = true;
};

function signin(serverUrl: string) {
    return async (dispatch: AppDispatch) => {
        const state = store.getState();
        dispatch(
            startFetch({
                id: OperationId.SignIn,
                message: state.uiReducer.translate('login_to_account_fetch'),
            }),
        );

        LocalStorageHelper.storeSetObject(
            LocalStorageHelper.storeKeys.SERVER_URL,
            serverUrl,
        );

        // To initiate a login do an anonymous POST request
        const result = await nextcloudApi.loginInit(
            state.appReducer.appConfig.stubUrl,
            serverUrl,
        );
        if (result.success === true) {
            logger.info('The action signin', 'Auth');
            const resultInit = result.result;

            // The url in login should be opened in the default browser,
            // this is where the user will follow the login procedure.
            window.open(resultInit.login, '_blank');

            // The program should directly start polling the poll endpoint
            runLoginChecker(dispatch, resultInit);

            dispatch(
                stopFetch({
                    operationId: OperationId.SignIn,
                }),
            );
        } else {
            dispatch({
                type: 'users.LOGIN_FAILED',
                payload: {
                    message: getCleanLoginErrorMessage(result.result.message),
                },
            });
            dispatch(
                stopFetch({
                    operationId: OperationId.SignIn,
                }),
            );
        }
    };
}

function logout(_isUI: boolean) {
    return async (dispatch: AppDispatch) => {
        logger.info('The action logout', 'Auth');
        const state = store.getState();

        dispatch(
            startFetch({
                id: OperationId.Logout,
                message: state.uiReducer.translate('logout_fetch'),
            }),
        );
        dispatch({ type: 'users.LOGOUT', payload: {} });
        UserStorageHelper.storeClearUser();
        appApi.clearCacheUserConfiguration();
        history.replace('/login');
        dispatch(
            stopFetch({
                operationId: OperationId.Logout,
            }),
        );
    };
}

// The instance's theming drives accents only: the pane keeps the product
// background. mapToFluentTokens reads `backgroundColor` ahead of the other
// background fields, and the text colour, `inverted` (hence the neutral
// palette) and the bright/dark element variant all follow from it.
function overrideInstanceBackground(
    nextcloudTheming: NextcloudThemingCapabilities | null,
): NextcloudThemingCapabilities | null {
    if (!nextcloudTheming) {
        return null;
    }

    return {
        ...nextcloudTheming,
        backgroundColor: DEFAULT_THEME_COLORS.appBg,
    };
}

// The one way a capabilities payload turns into stored theming. unknown, not
// a shape: the lib's applyThemingCapabilities does the parsing.
function readThemingCapabilities(
    ocs: unknown,
): NextcloudThemingCapabilities | null {
    return overrideInstanceBackground(Settings.applyThemingCapabilities(ocs));
}

async function fetchNextcloudTheming(
    stubUrl: string,
    user: NextcloudUser,
): Promise<NextcloudThemingCapabilities | null> {
    const capabilitiesResponse = await nextcloudApi.getCapabilities(
        stubUrl,
        user,
    );
    if (!capabilitiesResponse.success) {
        return null;
    }

    return readThemingCapabilities(capabilitiesResponse.result.ocs);
}

function loadUserConfiguration(user: NextcloudUser) {
    return async (dispatch: AppDispatch) => {
        const state = store.getState();
        logger.info('The action loadUserConfiguration', 'Auth');

        const userCache = appApi.getCacheUserConfiguration();
        if (!Utilities.isNANObject(userCache)) {
            userCache.userConfig.licenseStatus =
                getValidatedMappedLicenseStatusBodyCache(
                    userCache.userConfig.licenseStatus,
                );
            dispatch({
                type: authConstants.APP_CHANGE_USER_CONFIG,
                payload: { userConfig: userCache.userConfig },
            });
            dispatch({
                type: authConstants.APP_CHANGE_USER_SETTINGS,
                payload: {
                    userSettings: userCache.userSettings,
                    versionSettings: userCache.versionSettings,
                    filesSharingSettings: userCache.filesSharingSettings,
                    passwordPolicySettings: userCache.passwordPolicySettings,
                    // Paused site-wide (see below). To restore, uncomment
                    // (needs the `Theme` import back):
                    //
                    // theme:
                    //     new License(userCache.userConfig).Level >=
                    //     LicenseLevel.Plus
                    //         ? new Theme(userCache.theme)
                    //         : null,
                    theme: null,
                    // An older build's cache still holds the instance one.
                    nextcloudTheming: overrideInstanceBackground(
                        userCache.nextcloudTheming || null,
                    ),
                },
            });

            // setCacheUserConfiguration can drop the theming from the cache.
            if (!userCache.nextcloudTheming) {
                const nextcloudTheming = await fetchNextcloudTheming(
                    state.appReducer.appConfig.stubUrl,
                    user,
                );
                // A logout, another account, or a refresh that already loaded
                // the theming means this fetch is stale.
                const auth = store.getState().authentication;
                if (
                    nextcloudTheming &&
                    auth.user === user &&
                    !auth.nextcloudTheming
                ) {
                    dispatch({
                        type: authConstants.APP_CHANGE_NEXTCLOUD_THEMING,
                        payload: { nextcloudTheming },
                    });
                }
            }
        } else {
            dispatch(
                startFetch({
                    id: OperationId.LoadUserConfiguration,
                    message: state.uiReducer.translate('load_configuration'),
                }),
            );

            const [settingsConfig, userResponse, ...responses] =
                await Promise.all([
                    appApi.getSettingsConfig(),
                    nextcloudApi.getUser(
                        state.appReducer.appConfig.stubUrl,
                        user,
                    ),
                    nextcloudApi.getPing(
                        state.appReducer.appConfig.stubUrl,
                        user,
                    ),
                    nextcloudApi.getStatus(
                        state.appReducer.appConfig.stubUrl,
                        user,
                    ),
                    nextcloudApi.getLicenceStatus(
                        state.appReducer.appConfig.stubUrl,
                        user,
                    ),
                ]);

            if (!userResponse.success) {
                logger.info('The action logout', 'Auth');
                dispatch({ type: 'users.LOGOUT', payload: {} });
                UserStorageHelper.storeClearUser();
                appApi.clearCacheUserConfiguration();
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadUserConfiguration,
                    }),
                );
                return;
            }

            if (!areResponsesSuccessful(responses)) {
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadUserConfiguration,
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                        message: state.uiReducer.translate(
                            'load_user_configuration_error',
                        ),
                    }),
                );
                return;
            }

            const [pingResponse, statusResponse, licenseStatusResponse] =
                responses;

            const userConfig: UserConfig = {
                userInfo: userResponse.result.ocs.data,
                ping: pingResponse.result,
                status: statusResponse.result,
                licenseStatus: getValidatedMappedLicenseStatusBody(
                    licenseStatusResponse.result,
                    Assembly.MsTeamsAddin,
                ),
            };

            let userSettings = settingsConfig.settings;
            let filesSharingSettings = settingsConfig.filesSharing;
            let passwordPolicySettings = settingsConfig.passwordPolicy;
            let versionSettings = settingsConfig.version;
            // let, not const: restoring Advanced Theming below reassigns both.
            // eslint-disable-next-line prefer-const
            let themeSettings = settingsConfig.theme;
            // eslint-disable-next-line prefer-const
            let theme = null;
            let nextcloudTheming: NextcloudThemingCapabilities | null = null;

            let userServerSettings = [];

            const license = new License(userConfig);
            const {
                generalScope: { canUseServerApp },
            } = new LicensePermissions(license.Level);

            if (canUseServerApp) {
                const settingsValuesResponse =
                    await nextcloudApi.getSettingValues(
                        state.appReducer.appConfig.stubUrl,
                        user,
                    );

                if (!settingsValuesResponse.success) {
                    dispatch(
                        stopFetch({
                            operationId: OperationId.LoadUserConfiguration,
                            status: NotificationStatus.Error,
                            errorCode: AppErrorCode.CustomError,
                            message: state.uiReducer.translate(
                                'load_user_configuration_error',
                            ),
                        }),
                    );
                    return;
                }

                userServerSettings = settingsValuesResponse.result;
            }

            const capabilitiesResponse = await nextcloudApi.getCapabilities(
                state.appReducer.appConfig.stubUrl,
                user,
            );

            if (!capabilitiesResponse.success) {
                dispatch(
                    stopFetch({
                        operationId: OperationId.LoadUserConfiguration,
                        status: NotificationStatus.Error,
                        errorCode: AppErrorCode.CustomError,
                        message: state.uiReducer.translate(
                            'load_user_configuration_error',
                        ),
                    }),
                );
                return;
            }

            const userCapabilities = capabilitiesResponse.result;

            // Apply server settings
            let settings = new Settings(userSettings);
            userSettings = settings.applyServerSettings(
                userSettings,
                userServerSettings,
            );

            // Unlike Outlook, no license gate — always show native theming.
            nextcloudTheming = readThemingCapabilities(userCapabilities.ocs);

            // Sendent's own Advanced (server-settings) Theming — separate
            // from the Nextcloud-native theming above, which has no license
            // gate here — is paused site-wide for now, regardless of
            // license: `theme` stays null. To restore, uncomment (needs the
            // `Theme` import back):
            //
            // if (license.Level >= LicenseLevel.Plus) {
            //     themeSettings = settings.applyTheme(
            //         settingsConfig.theme,
            //         userServerSettings,
            //     );
            //     theme = new Theme(themeSettings);
            // }

            // Apply license settings
            settings = new Settings(userSettings);
            if (!settings.DisableSettings) {
                setSettingFlag(settings, 'General', 'display');
                setSettingFlag(settings, 'SendMode', 'display');
                setSettingFlag(settings, 'DisableSettings', 'display');
                setSettingFlag(settings, 'AttachmentSettings', 'display');
                setSettingFlag(settings, 'InterceptMode', 'display');
                setSettingFlag(settings, 'Threshold', 'display');
                setSettingFlag(
                    settings,
                    'ShareFilesAndPublicFolder',
                    'display',
                );
                setSettingFlag(settings, 'HtmlSnippetUploadFiles', 'display');
                setSettingFlag(settings, 'HtmlSnippetPublicShare', 'display');
                setSettingFlag(settings, 'SecureMail', 'display');
                setSettingFlag(settings, 'SecureMailEnabled', 'display');
                setSettingFlag(settings, 'SecureMailEnforced', 'display');
                setSettingFlag(settings, 'HtmlSnippetSecureMail', 'display');
                setSettingFlag(settings, 'GuestAccounts', 'display');
                setSettingFlag(settings, 'GuestAccountsEnabled', 'display');
                setSettingFlag(settings, 'GuestAccountsEnforced', 'display');
                setSettingFlag(settings, 'Talk', 'display');
                setSettingFlag(settings, 'TalkEnabled', 'display');
                setSettingFlag(settings, 'GenerateTalkPassword', 'display');

                if (license.Level >= LicenseLevel.Standard) {
                    setSettingFlag(settings, 'SendMode', 'enabled');
                    setSettingFlag(settings, 'InterceptMode', 'enabled');
                    setSettingFlag(settings, 'Threshold', 'enabled');
                }
                if (license.Level >= LicenseLevel.Plus) {
                    setSettingFlag(
                        settings,
                        'HtmlSnippetUploadFiles',
                        'enabled',
                    );
                    setSettingFlag(
                        settings,
                        'HtmlSnippetPublicShare',
                        'enabled',
                    );
                    setSettingFlag(settings, 'SecureMailEnabled', 'enabled');
                    setSettingFlag(settings, 'SecureMailEnforced', 'enabled');
                    setSettingFlag(
                        settings,
                        'HtmlSnippetSecureMail',
                        'enabled',
                    );
                    setSettingFlag(settings, 'GuestAccountsEnabled', 'enabled');
                    setSettingFlag(
                        settings,
                        'GuestAccountsEnforced',
                        'enabled',
                    );
                }
                setSettingFlag(settings, 'TalkEnabled', 'enabled');
                setSettingFlag(settings, 'GenerateTalkPassword', 'enabled');
            }

            versionSettings = settings.applyVersionSettings(
                versionSettings,
                userCapabilities.ocs,
            );
            logger.debug(
                `versionSettings: ${JSON.stringify(versionSettings)}`,
                'Auth',
            );
            filesSharingSettings = settings.applyFileSharingSettings(
                filesSharingSettings,
                userCapabilities.ocs,
            );
            logger.debug(
                `filesSharingSettings: ${JSON.stringify(filesSharingSettings)}`,
                'Auth',
            );
            passwordPolicySettings = settings.applyPasswordPolicySettings(
                passwordPolicySettings,
                userCapabilities.ocs,
            );
            logger.debug(
                // eslint-disable-next-line max-len
                `passwordPolicySettings: ${JSON.stringify(passwordPolicySettings)}`,
                'Auth',
            );

            dispatch({
                type: authConstants.APP_CHANGE_USER_CONFIG,
                payload: { userConfig: userConfig },
            });
            logger.debug(
                `userSettings: ${JSON.stringify(userSettings)}`,
                'Auth',
            );
            dispatch({
                type: authConstants.APP_CHANGE_USER_SETTINGS,
                payload: {
                    userSettings: userSettings,
                    versionSettings,
                    filesSharingSettings,
                    passwordPolicySettings,
                    theme,
                    nextcloudTheming,
                },
            });

            const userCache = {
                dateAdded: new Date().toISOString(),
                userConfig: userConfig,
                userSettings: userSettings,
                versionSettings: versionSettings,
                filesSharingSettings: filesSharingSettings,
                passwordPolicySettings: passwordPolicySettings,
                theme: themeSettings,
                nextcloudTheming: nextcloudTheming,
            };
            appApi.setCacheUserConfiguration(userCache);

            dispatch(
                stopFetch({
                    operationId: OperationId.LoadUserConfiguration,
                }),
            );
        }
    };
}

const loginCheckerThread = async (
    dispatch: AppDispatch,
    resultInit: LoginInitResult,
) => {
    const state = store.getState();
    try {
        logger.info('loginCheckerThread start', 'Auth');
        const result = await nextcloudApi.loginCheck(
            state.appReducer.appConfig.stubUrl,
            resultInit.poll,
        );
        if (result.success === true) {
            dispatch({
                type: authConstants.LOGIN_PROCESS,
                payload: { user: result.result },
            });
            dispatch({ type: 'users.LOGIN_SUCCESS', payload: {} });
            UserStorageHelper.storeSetUser(result.result, true);
            // dispatch({ type:'app.APP_STOP_FETCH', payload: { } });

            return;
        }
    } catch (ex) {}

    // Continue thread
    setTimeout(function run() {
        loginCheckerThread(dispatch, resultInit);
    }, state.appReducer.appConfig.pollInterval * 1000);
};

/**
 * Runs the function to check logins
 */
const runLoginChecker = (
    dispatch: AppDispatch,
    resultInit: LoginInitResult,
) => {
    logger.info('runSessionChecker called', 'Auth');
    const state = store.getState();
    setTimeout(function run() {
        loginCheckerThread(dispatch, resultInit);
    }, state.appReducer.appConfig.pollInterval * 1000);
};

function refreshUserConfiguration(user: NextcloudUser) {
    return async (dispatch: AppDispatch) => {
        logger.info('The action refreshUserConfiguration', 'Auth');
        appApi.clearCacheUserConfiguration();
        dispatch(loadUserConfiguration(user));
    };
}

export const authActions = {
    logout,
    signin,
    loadUserConfiguration,
    refreshUserConfiguration,
};
